import React, { useMemo, forwardRef } from 'react'
import { IconContext } from 'react-icons'
import { IoArrowForwardSharp } from 'react-icons/io5'
import styled, { css } from 'styled-components'
import { tagOutboundURL } from 'utils/tagOutboundURL'
import { Text, Props as TextProps } from 'lib/Text'

const Container = styled.span`
  margin-bottom: 8px;
`

const Anchor = styled.a<{ arrowPosition: 'left' | 'right' }>`
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;

  ${(props) =>
    props.arrowPosition === 'right' &&
    css`
      flex-direction: row-reverse;
    `}
`

const LinkText = styled(Text)<{ underlined?: boolean }>`
  ${(props) =>
    props.underlined &&
    css`
      box-shadow: 0px 1px 0px black;
    `}

  :hover {
    box-shadow: 0px 2px 0px black;
  }
`

interface Props {
  url: string
  label?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
  isExternalUrl?: boolean
  textProps?: TextProps
  arrowPosition?: 'left' | 'right'
  underlined?: boolean
  className?: string
}

const DEFAULT_TEXT_PROPS: TextProps = {
  weight: 'medium',
  size: 'p'
}

const ExternalLink = forwardRef(({ url, label, onClick, arrowPosition, textProps, underlined }: Props, ref: React.Ref<any>) => {
  const formattedUrl = useMemo(() => {
    try {
      return new URL(url).hostname
    } catch {
      return url
    }
  }, [url])

  if (!url) {
    return null
  }

  return (
    <Anchor arrowPosition={arrowPosition || 'left'} href={tagOutboundURL(url)} target="_blank" rel="noopener" ref={ref} onClick={onClick}>
      <IoArrowForwardSharp />
      <LinkText {...(textProps || DEFAULT_TEXT_PROPS)} as="span" underlined={underlined}>
        {label ? label : formattedUrl}
      </LinkText>
    </Anchor>
  )
})
ExternalLink.displayName = 'ExternalLink'

const InternalLink = forwardRef(({ url, label, onClick, arrowPosition, textProps, underlined }: Props, ref: React.Ref<any>) => {
  return (
    <Anchor arrowPosition={arrowPosition || 'left'} href={url} ref={ref} onClick={onClick}>
      <IoArrowForwardSharp />
      <LinkText {...(textProps || DEFAULT_TEXT_PROPS)} as="span" underlined={underlined}>
        {label}
      </LinkText>
    </Anchor>
  )
})
InternalLink.displayName = 'InternalLink'

export const ArrowLink = forwardRef((props: Props, ref: React.Ref<any>) => {
  const { isExternalUrl = false, className } = props

  return (
    <IconContext.Provider value={{ size: props.textProps?.size === 'ps' ? '16' : '24', color: '#000' }}>
      <Container className={className}>{isExternalUrl ? <ExternalLink {...props} ref={ref} /> : <InternalLink {...props} />}</Container>
    </IconContext.Provider>
  )
})
ArrowLink.displayName = 'ArrowLink'
